// Cross-trip reservation aggregation — ports mobile/lib/all-reservations.ts +
// mobile/lib/use-book-today.ts. The "global reservation tracker" fans out over
// every upcoming/active, non-draft trip, fetches each trip's /state, flattens
// `reservations[]`, and tags each row with its owning trip and that trip's
// bookings and stays (the secured-by-booking rule and the venue's zone need
// them). Powers both the homepage ReservationTeaser CTA and the
// /reservations agenda. Rows, sort and counts are lib/reservation-view.ts.

import { getMe } from './api';
import { getTripState } from './trip-state';
import { localDateISO } from './dates';
import { sortByOpening, trackerRows, windowLine, type TrackerRow } from './reservation-view';
import type { Booking, Reservation, TripListItem, TripStay } from './types';

export interface TaggedReservation extends Reservation {
  tripId: string;
  tripName: string;
  /** The viewer's role on the trip; only an owner can write its state. */
  tripRole: string;
  tripStart: string | null;
  tripEnd: string | null;
  tripBookings: Booking[];
  tripStays: TripStay[];
}

// Mirror mobile's loadAllReservations filter: drop drafts; include undated
// trips; otherwise include only trips that haven't fully ended. (/api/me
// already omits soft-deleted trips, so there's no `deleted` to check.)
function includeTrip(t: TripListItem, todayISO: string): boolean {
  if (t.draft) return false;
  const start = t.dates?.lockedStart;
  const end = t.dates?.lockedEnd;
  if (!start && !end) return true;
  if (end) return end >= todayISO;
  return true;
}

export async function loadAllReservations(): Promise<TaggedReservation[]> {
  const me = await getMe();
  const today = localDateISO();
  const trips = me.trips.filter((t) => includeTrip(t, today));
  const lists = await Promise.all(
    trips.map(async (t) => {
      const state = await getTripState(t.tripId).catch(() => null);
      const reservations = (state?.reservations ?? []) as Reservation[];
      const bookings = (state?.bookings ?? []) as Booking[];
      const stays = (state?.stays ?? t.stays ?? []) as TripStay[];
      return reservations
        .filter((r) => r && typeof r === 'object')
        .map(
          (r): TaggedReservation => ({
            ...r,
            tripId: t.tripId,
            tripName: t.name || t.shortName || t.tripId,
            tripRole: t.role,
            tripStart: t.dates?.lockedStart ?? null,
            tripEnd: t.dates?.lockedEnd ?? null,
            tripBookings: bookings,
            tripStays: stays,
          }),
        );
    }),
  );
  return lists.flat();
}

/** Tracker rows across trips, soonest opening first. */
export function allTrackerRows(list: TaggedReservation[]): TrackerRow<TaggedReservation>[] {
  const today = localDateISO();
  const rows: TrackerRow<TaggedReservation>[] = [];
  for (const r of list) {
    rows.push(...trackerRows([r], { bookings: r.tripBookings, stays: r.tripStays, today }));
  }
  return sortByOpening(rows);
}

export interface BookTodaySummary {
  count: number; // windows open now across all trips
  tripNames: string[]; // distinct trips with a book-now item
  next: { name: string; tripName: string; days: number; line: string } | null;
  needsDate: number; // to book, but no table date yet
  trackedTotal: number; // not dismissed / cancelled
}

// Count book-now, find the soonest window still ahead, count the undated.
// Secured entries (status or a matching booking) never count as next.
export function summarizeBookToday(list: TaggedReservation[]): BookTodaySummary {
  const rows = allTrackerRows(list);
  const tripSet = new Set<string>();
  let count = 0;
  let needsDate = 0;
  let next: BookTodaySummary['next'] = null;
  for (const row of rows) {
    if (row.state === 'book') {
      count++;
      tripSet.add(row.res.tripName);
    } else if (row.state === 'queued') {
      const days = row.countdown.daysFromToday;
      if (row.countdown.reason === 'needs-date') needsDate++;
      else if (days != null && days > 0 && (!next || days < next.days)) {
        next = { name: row.res.name || row.res.refId || 'Reservation', tripName: row.res.tripName, days, line: windowLine(row) };
      }
    }
  }
  return { count, tripNames: [...tripSet], next, needsDate, trackedTotal: rows.length };
}
